import React, { useEffect, useRef, useState } from 'react';
import { CreditCard, MoreVertical } from 'lucide-react';
import { useAuthStore } from '@/Stores/auth-v2-store';
import { useActivateSociety, useDeactivateSociety } from '@/features/societies/hooks/useSocieties';

export interface SocietyActionsMenuSociety {
  uuid: string;
  isActive: boolean;
}

export interface SocietyActionsMenuProps {
  society: SocietyActionsMenuSociety;
  /**
   * Callback pour ouvrir la modale de création de compte Stripe.
   * Si non fourni, l'option "Créer compte Stripe" n'est pas affichée.
   * Le parent calcule la condition d'éligibilité (stripeAccountId, billedId, etc.)
   * et ne passe la callback que si l'action est applicable.
   */
  onCreateStripeClick?: () => void;
  /** 'header' : utilisé dans le bandeau violet de la page détail (texte blanc).
   *  'compact' : utilisé dans une cellule de tableau (icône seule). */
  variant?: 'header' | 'compact';
}

export const SocietyActionsMenu: React.FC<SocietyActionsMenuProps> = ({
  society,
  onCreateStripeClick,
  variant = 'compact',
}) => {
  const { hasPermission } = useAuthStore();
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const { mutate: activateSociety } = useActivateSociety();
  const { mutate: deactivateSociety } = useDeactivateSociety();

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const canToggle = hasPermission('society.toggle_status');
  const canCreateStripe = Boolean(onCreateStripeClick) && hasPermission('stripe.account');

  if (!canToggle && !canCreateStripe) return null;

  const triggerClass =
    variant === 'header'
      ? 'tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-border tw-border-white/30 tw-rounded-lg tw-text-sm tw-font-medium tw-text-white tw-bg-white/10 hover:tw-bg-white/20 tw-transition-colors'
      : 'tw-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded hover:tw-bg-gray-100 tw-text-gray-600 tw-transition-colors';

  const wrapperClass = variant === 'header' ? 'tw-ml-auto tw-relative' : 'tw-relative';

  const handleToggle = () => {
    if (society.isActive) {
      deactivateSociety(society.uuid);
    } else {
      activateSociety(society.uuid);
    }
    setIsOpen(false);
  };

  return (
    <div className={wrapperClass} ref={ref}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className={triggerClass}
        aria-label="Actions"
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        {variant === 'header' && <span className="tw-hidden md:tw-inline">Actions</span>}
        <MoreVertical className="tw-w-4 tw-h-4" />
      </button>

      {isOpen && (
        <div
          role="menu"
          className="tw-absolute tw-right-0 tw-mt-2 tw-w-56 tw-bg-white tw-rounded-lg tw-shadow-lg tw-border tw-border-gray-200 tw-py-1 tw-z-10"
        >
          {canCreateStripe && (
            <button
              type="button"
              role="menuitem"
              onClick={(e) => {
                e.stopPropagation();
                onCreateStripeClick?.();
                setIsOpen(false);
              }}
              className="tw-w-full tw-flex tw-items-center tw-gap-3 tw-px-4 tw-py-2 tw-text-sm tw-text-affinicar-primary hover:tw-bg-affinicar-lighter tw-transition-colors"
            >
              <CreditCard className="tw-w-4 tw-h-4" />
              <span>Créer compte Stripe</span>
            </button>
          )}
          {canToggle && (
            <button
              type="button"
              role="menuitem"
              onClick={(e) => {
                e.stopPropagation();
                handleToggle();
              }}
              className={`tw-w-full tw-flex tw-items-center tw-gap-3 tw-px-4 tw-py-2 tw-text-sm tw-transition-colors ${
                society.isActive
                  ? 'tw-text-red-600 hover:tw-bg-red-50'
                  : 'tw-text-green-600 hover:tw-bg-green-50'
              }`}
            >
              <span>{society.isActive ? 'Désactiver' : 'Activer'}</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};
